import Api from '../Api/Api';
import UserProfile from '../Common/UserProfile';

var JobReviewController = (function () {
  let getJobReviewsInflight = null;

  function computeReviewsInfo(jobs, applicantsByJobId, userId) {
    let reviewsToDo = 0;
    let reviewsDone = 0;

    if (UserProfile.isReviewer()) {
      jobs.forEach((job) => {
        const applicants = applicantsByJobId[job.id] || [];
        const stageId = job.review_config && job.review_config.stage_id;

        applicants.forEach((applicant) => {
          if (stageId !== applicant.pipeline_stage) return;

          reviewsToDo += 1;
          const applicantReview = applicant.review;
          const hasReviewed =
            applicantReview !== null &&
            !(applicantReview[userId] === null || applicantReview[userId] === undefined);

          if (hasReviewed) reviewsDone += 1;
        });
      });
    } else if (UserProfile.isRecruiter()) {
      jobs.forEach((job) => {
        const reviewers = job.reviewers || [];
        const applicants = applicantsByJobId[job.id] || [];
        const stageId = job.review_config && job.review_config.stage_id;
        let applicantsCount = 0;

        applicants.forEach((applicant) => {
          if (stageId !== applicant.pipeline_stage) return;

          applicantsCount += 1;
          const applicantReview = applicant.review;
          if (applicantReview) {
            const intersection = Object.keys(applicantReview).filter((x) => reviewers.includes(x));
            reviewsDone += intersection.length;
          }
        });

        reviewsToDo += applicantsCount * reviewers.length;
      });
    }

    return { reviewsToDo, reviewsDone };
  }

  var getJobReviews = function (cb) {
    if (getJobReviewsInflight) {
      getJobReviewsInflight.then(
        (info) => cb(null, info),
        () => cb('Error on fetching data')
      );

      return;
    }

    const userId = UserProfile.getId();
    const reviewFilter = UserProfile.isRecruiter()
      ? { user_recruiter: userId }
      : UserProfile.isReviewer()
      ? { reviewers: { contains: [userId.toString()] } }
      : {};

    getJobReviewsInflight = new Promise((resolve, reject) => {
      const api = new Api();
      api.get(
        'Jobs',
        {
          fields: ['id', 'review_config', 'reviewers'],
          where: {
            status: 'PUBLISHED',
            ...reviewFilter,
            has_review_stage: true
          }
        },
        (status, jobs) => {
          if (status > 400 || !Array.isArray(jobs)) {
            reject(new Error('jobs'));
            return;
          }

          if (jobs.length === 0) {
            resolve({ reviewsToDo: 0, reviewsDone: 0 });
            return;
          }

          const jobIds = jobs.map((j) => j.id);
          const stageIds = [
            ...new Set(
              jobs
                .map((j) => j.review_config && j.review_config.stage_id)
                .filter((id) => id !== null && id !== undefined)
            )
          ];

          api.get(
            'Applicants',
            {
              fields: ['id', 'job_id', 'pipeline_stage', 'review'],
              where: {
                job_id: { inq: jobIds },
                ...(stageIds.length ? { pipeline_stage: { inq: stageIds } } : {}),
                deleted: false
              }
            },
            (appStatus, applicants) => {
              if (appStatus > 400) {
                reject(new Error('applicants'));
                return;
              }
              const byJob = {};
              (Array.isArray(applicants) ? applicants : []).forEach((a) => {
                if (!byJob[a.job_id]) byJob[a.job_id] = [];
                byJob[a.job_id].push(a);
              });
              resolve(computeReviewsInfo(jobs, byJob, userId));
            }
          );
        }
      );
    }).finally(() => {
      getJobReviewsInflight = null;
    });

    getJobReviewsInflight.then(
      (info) => cb(null, info),
      () => cb('Error on fetching data')
    );
  };

  var getJobsToReview = function (cb) {
    let api = new Api();
    const reviewerId = UserProfile.getId();

    api.getCount(
      'Jobs',
      {
        status: 'PUBLISHED',
        reviewers: {
          contains: [reviewerId.toString()]
        }
      },
      (status, data) => {
        if (status <= 400 && data && typeof data.count === 'number') {
          return cb(null, data.count);
        }
        return cb('Error on fetching data');
      }
    );
  };

  var getJobReviewsByReviewer = function (applicants, review_config) {
    let reviewsToDo = 0;
    let reviewsDone = 0;
    const reviewerId = UserProfile.getId();

    applicants.forEach((applicant) => {
      if (review_config.stage_id === applicant.pipeline_stage) {
        reviewsToDo += 1;

        const applicantReview = applicant.review;
        const hasReviewed =
          applicantReview !== null &&
          !(applicantReview[reviewerId] === null || applicantReview[reviewerId] === undefined);

        if (hasReviewed) {
          reviewsDone += 1;
        }
      }
    });

    const reviewsInfo = {
      reviewsToDo,
      reviewsDone
    };
    return reviewsInfo;
  };

  var getJobReviewsByRecruiter = function (applicants, reviewers) {
    const reviewsToDo = reviewers.length * applicants.length;
    let reviewsDone = 0;

    applicants.forEach((applicant) => {
      const applicantReview = applicant.review;

      if (applicantReview) {
        const applicantReviewKeys = Object.keys(applicantReview);
        const intersection = applicantReviewKeys.filter((x) => reviewers.includes(x));

        reviewsDone += intersection.length;
      }
    });

    const reviewsInfo = {
      reviewsToDo,
      reviewsDone
    };
    return reviewsInfo;
  };

  var getJobReviewsByApplicant = function (applicant, reviewers) {
    const reviewsToDo = reviewers.length;
    let reviewsDone = 0;

    const applicantReview = applicant.review;

    if (applicantReview) {
      const applicantReviewKeys = Object.keys(applicantReview);
      const intersection = applicantReviewKeys.filter((x) => reviewers.includes(x));

      reviewsDone += intersection.length;
    }

    const reviewsInfo = {
      reviewsToDo,
      reviewsDone
    };
    return reviewsInfo;
  };

  var getJobReviewsByJob = function (applicants, reviewers) {
    const applicantToBeReviewed = applicants.filter((applicant) => applicant.review_completed >= 0);
    const reviewsToDo = applicantToBeReviewed.length;
    let reviewsDone = 0;

    applicants.forEach((applicant) => {
      const applicantReview = applicant.review;

      if (applicantReview) {
        const applicantReviewKeys = Object.keys(applicantReview);
        const intersection = applicantReviewKeys.filter((x) => reviewers.includes(x));

        if (intersection.length === reviewers.length) {
          reviewsDone += 1;
        }
      }
    });

    const reviewsInfo = {
      reviewsToDo,
      reviewsDone
    };

    return reviewsInfo;
  };

  var getIncompleteReviewsInfo = function (applicants, reviewers) {
    let incompleteReviews = false;
    applicants.forEach((applicant) => {
      const applicantReview = applicant.review;

      if (applicantReview) {
        const applicantReviewKeys = Object.keys(applicantReview);
        const intersection = applicantReviewKeys.filter((x) => reviewers.includes(x));

        if (intersection.length < reviewers.length) {
          incompleteReviews = true;
        }
      } else {
        incompleteReviews = true;
      }
    });

    return incompleteReviews;
  };

  return {
    getJobReviews: getJobReviews,
    getJobReviewsByReviewer: getJobReviewsByReviewer,
    getJobReviewsByRecruiter: getJobReviewsByRecruiter,
    getJobReviewsByApplicant: getJobReviewsByApplicant,
    getJobReviewsByJob: getJobReviewsByJob,
    getIncompleteReviewsInfo: getIncompleteReviewsInfo,
    getJobsToReview: getJobsToReview
  };
})();

export default JobReviewController;
